import React, { useCallback, useState } from 'react';
import cn from 'classnames';
import { useAtomValue } from 'jotai/utils';
import { chatStreamingAtom } from 'src/shared/chat';
import { useChatSend } from './useChatSend';
import { getToolLabel } from './actionLabels';
import type { ChatPlan } from './types';
import styles from './PlanCard.module.scss';

interface PlanCardProps {
  plan: ChatPlan;
}

// Show the "process first N" field when there is a real choice to make.
const LIMIT_FIELD_MIN_ASSETS = 5;

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function scopeText(plan: ChatPlan): string {
  if (plan.matchedCount && plan.matchedCount > plan.assetCount) {
    return `Found ${plan.matchedCount} · will process ${plan.assetCount}`;
  }
  return plural(plan.assetCount, 'asset');
}

function statusBadge(plan: ChatPlan): { label: string; tone: string } {
  switch (plan.status) {
    case 'pending':
      return { label: 'Needs approval', tone: styles.badgePending };
    case 'approved': {
      const limit = plan.assetLimit ?? plan.assetCount;
      const scope = limit < plan.assetCount ? `first ${limit} of ${plan.assetCount}` : plural(limit, 'asset');
      return {
        label: plan.autoApproved ? `Auto-approved · ${scope}` : `Approved · ${scope}`,
        tone: styles.badgeApproved,
      };
    }
    case 'declined':
      return { label: 'Cancelled', tone: styles.badgeMuted };
    case 'expired':
      return { label: 'Expired — ask again', tone: styles.badgeMuted };
    case 'replaced':
      return { label: 'Replaced by a newer plan', tone: styles.badgeMuted };
  }
}

const APPROVAL_REASONS: Record<string, string> = {
  over_asset_floor: 'Run mode still asks for plans over 25 assets.',
  deletes_fields: 'Run mode still asks before deleting fields.',
};

/**
 * Plan proposed by the agent before it changes assets or runs an addon
 * (issue #89). Pending plans offer Approve / Cancel and, for larger
 * batches, "only the first N"; the answer is sent as the next chat
 * message with a plan_decision the proxy enforces.
 */
export const PlanCard: React.FC<PlanCardProps> = ({ plan }) => {
  const isStreaming = useAtomValue(chatStreamingAtom);
  const { send } = useChatSend();
  const [limit, setLimit] = useState(plan.assetCount);
  const [limitError, setLimitError] = useState<string | null>(null);

  const pending = plan.status === 'pending';
  const showLimit = pending && plan.assetCount >= LIMIT_FIELD_MIN_ASSETS;
  const badge = statusBadge(plan);
  const reason = pending && plan.approvalReason ? APPROVAL_REASONS[plan.approvalReason] : null;

  const approve = useCallback(() => {
    if (isStreaming) return;
    if (!Number.isInteger(limit) || limit < 1 || limit > plan.assetCount) {
      setLimitError(`Enter a number from 1 to ${plan.assetCount}`);
      return;
    }
    const partial = limit < plan.assetCount;
    void send(partial ? `Approved for the first ${plural(limit, 'asset')}` : 'Approved', {
      planDecision: {
        plan_id: plan.id,
        decision: 'approve',
        ...(partial ? { asset_limit: limit } : {}),
      },
    });
  }, [isStreaming, limit, plan.assetCount, plan.id, send]);

  const cancel = useCallback(() => {
    if (isStreaming) return;
    void send('Cancel the plan', { planDecision: { plan_id: plan.id, decision: 'reject' } });
  }, [isStreaming, plan.id, send]);

  return (
    <div className={cn(styles.card, pending && styles.cardPending)} data-testid="plan-card">
      <div className={styles.header}>
        <span className={styles.title}>Plan</span>
        <span className={cn(styles.badge, badge.tone)}>{badge.label}</span>
      </div>

      <p className={styles.summary}>{plan.summary}</p>

      {plan.steps.length > 0 && (
        <ol className={styles.steps}>
          {plan.steps.map((step, i) => (
            <li key={i}>
              <span className={styles.stepTool}>{getToolLabel(step.tool)}</span>
              {step.description && <span className={styles.stepDesc}> · {step.description}</span>}
            </li>
          ))}
        </ol>
      )}

      <div className={styles.scope}>{scopeText(plan)}</div>
      {reason && <div className={styles.reason}>{reason}</div>}

      {pending && (
        <>
          {showLimit && (
            <label className={styles.limitRow}>
              Process the first
              <input
                type="number"
                className={styles.limitInput}
                min={1}
                max={plan.assetCount}
                step={1}
                value={Number.isNaN(limit) ? '' : limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setLimitError(null);
                }}
                aria-label="Number of assets to process"
              />
              of {plan.assetCount}
            </label>
          )}
          {limitError && (
            <div className={styles.limitError} role="alert">
              {limitError}
            </div>
          )}
          <div className={styles.actions}>
            <button type="button" className={styles.approveBtn} onClick={approve} disabled={isStreaming}>
              Approve
            </button>
            <button type="button" className={styles.cancelBtn} onClick={cancel} disabled={isStreaming}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};
